import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Input,
  Button,
  Typography,
  Space,
  Avatar,
  Divider,
  Tag,
  Select,
  Switch,
  Modal,
  List
} from 'antd';
import {
  ArrowLeftOutlined,
  SendOutlined,
  RobotOutlined,
  UserOutlined,
  FunctionOutlined,
  FlagOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  citations?: any[];
  flagged?: boolean;
}

interface AnalysisFunction {
  name: string;
  description: string;
  parameters: string[];
}

const InsightConsole: React.FC = () => {
  const { studyId } = useParams();
  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [useRAG, setUseRAG] = useState(true);
  const [useFunctions, setUseFunctions] = useState(true);
  const [functionModalVisible, setFunctionModalVisible] = useState(false);
  const [availableFunctions, setAvailableFunctions] = useState<AnalysisFunction[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Load chat history and available functions
    loadChatHistory();
    loadAvailableFunctions();
  }, [studyId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadChatHistory = async () => {
    try {
      // Mock chat history
      const mockMessages: ChatMessage[] = [
        {
          id: '1',
          role: 'assistant',
          content: 'Welcome to the Insight Console! I can help you analyze your study data. Ask me questions about character representation, themes, or request specific analysis functions.',
          timestamp: new Date(),
        }
      ];
      setMessages(mockMessages);
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const loadAvailableFunctions = async () => {
    try {
      const mockFunctions: AnalysisFunction[] = [
        {
          name: 'calculate_representation_metrics',
          description: 'Calculate representation metrics across different demographics',
          parameters: ['media_types', 'time_period']
        },
        {
          name: 'analyze_character_arcs',
          description: 'Analyze character development arcs',
          parameters: ['character_types', 'arc_length']
        },
        {
          name: 'extract_themes_by_category',
          description: 'Extract themes filtered by content category',
          parameters: ['categories', 'theme_types']
        },
        {
          name: 'generate_sentiment_timeline',
          description: 'Generate sentiment analysis over time',
          parameters: ['granularity', 'sentiment_types']
        }
      ];
      setAvailableFunctions(mockFunctions);
    } catch (error) {
      console.error('Failed to load functions:', error);
    }
  };

  const sendMessage = async () => {
    if (!currentMessage.trim()) return;

    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: currentMessage,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setCurrentMessage('');
    setLoading(true);

    try {
      // Mock AI response
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const assistantMessage: ChatMessage = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: generateMockResponse(currentMessage),
        timestamp: new Date(),
        citations: useRAG ? [
          { chunk_id: 'chunk_1', media_title: 'Sample Media', confidence: 0.85 }
        ] : undefined,
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockResponse = (question: string): string => {
    const lowerQuestion = question.toLowerCase();
    
    if (lowerQuestion.includes('father') || lowerQuestion.includes('representation')) {
      return 'Based on the analysis, fathers in your dataset are primarily portrayed as protective and mentor figures. The representation shows a 60-40 male-female split in parental roles, with fathers often taking on traditional provider archetypes. Would you like me to run a detailed representation analysis function?';
    } else if (lowerQuestion.includes('theme') || lowerQuestion.includes('topic')) {
      return 'The primary themes identified include family bonds (85% frequency), personal growth (78% frequency), and social justice (45% frequency). Family relationships appear to be the dominant narrative thread. I can extract more detailed themes by category if needed.';
    } else if (lowerQuestion.includes('sentiment') || lowerQuestion.includes('emotion')) {
      return 'The emotional arc analysis shows a typical narrative structure with neutral opening (0.2), conflict development (-0.3 to -0.6), and positive resolution (0.8). The average sentiment is slightly positive at 0.1. Would you like a detailed sentiment timeline?';
    } else {
      return 'I can help you analyze various aspects of your study data. Try asking about character representation, themes, sentiment patterns, or request specific analysis functions. What would you like to explore?';
    }
  };

  const flagMessageForReport = (messageId: string) => {
    setMessages(prev => 
      prev.map(msg => 
        msg.id === messageId 
          ? { ...msg, flagged: !msg.flagged }
          : msg
      )
    );
  };

  const callAnalysisFunction = async (functionName: string) => {
    const functionMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'assistant',
      content: `Running ${functionName}...\n\nResults:\n• Total characters analyzed: 45\n• Gender breakdown: 60% male, 35% female, 5% non-binary\n• Representation gaps identified: Underrepresentation in leadership roles\n• Confidence score: 89%`,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, functionMessage]);
    setFunctionModalVisible(false);
  };

  const quickQuestions = [
    "What are the main themes in this study?",
    "Analyze character representation gaps",
    "Show sentiment analysis over time",
    "What archetypes are most common?"
  ];

  return (
    <div style={{ height: 'calc(100vh - 180px)', display: 'flex', flexDirection: 'column' }}>
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/dashboard')}
          >
            Back to Dashboard
          </Button>
        </Space>
        <Title level={2} style={{ marginTop: 16, marginBottom: 8 }}>
          Insight Console
        </Title>
        <Paragraph>
          Interactive AI analysis for Study: {studyId}
        </Paragraph>
      </div>

      {/* Settings */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space>
          <Text>RAG Context:</Text>
          <Switch 
            checked={useRAG} 
            onChange={setUseRAG}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
          <Divider type="vertical" />
          <Text>Functions:</Text>
          <Switch 
            checked={useFunctions} 
            onChange={setUseFunctions}
            checkedChildren="ON"
            unCheckedChildren="OFF"
          />
          <Divider type="vertical" />
          <Button 
            size="small"
            icon={<FunctionOutlined />}
            onClick={() => setFunctionModalVisible(true)}
          >
            Call Function
          </Button>
        </Space>
      </Card>

      {/* Chat Messages */}
      <Card 
        style={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          marginBottom: 16
        }}
        bodyStyle={{ 
          flex: 1, 
          padding: 0,
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: 16,
          maxHeight: '500px'
        }}>
          {messages.map((message) => (
            <div key={message.id} style={{ marginBottom: 16 }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'flex-start',
                justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start'
              }}>
                {message.role === 'assistant' && (
                  <Avatar 
                    icon={<RobotOutlined />} 
                    style={{ backgroundColor: '#722ed1', marginRight: 8 }}
                  />
                )}
                
                <div style={{ 
                  maxWidth: '70%',
                  order: message.role === 'user' ? -1 : 1
                }}>
                  <div style={{
                    background: message.role === 'user' ? '#722ed1' : '#f5f5f5',
                    color: message.role === 'user' ? 'white' : 'black',
                    padding: '12px 16px',
                    borderRadius: 12,
                    whiteSpace: 'pre-wrap'
                  }}>
                    {message.content}
                  </div>
                  
                  <div style={{ 
                    marginTop: 4, 
                    fontSize: 12, 
                    color: '#999',
                    textAlign: message.role === 'user' ? 'right' : 'left'
                  }}>
                    <Space size="small">
                      {message.timestamp.toLocaleTimeString()}
                      {message.citations && (
                        <Tag color="blue">
                          {message.citations.length} sources
                        </Tag>
                      )}
                      {message.role === 'assistant' && (
                        <Button
                          type="text"
                          size="small"
                          icon={<FlagOutlined />}
                          style={{ 
                            color: message.flagged ? '#52c41a' : '#999',
                            padding: 0
                          }}
                          onClick={() => flagMessageForReport(message.id)}
                        />
                      )}
                    </Space>
                  </div>
                </div>

                {message.role === 'user' && (
                  <Avatar 
                    icon={<UserOutlined />} 
                    style={{ backgroundColor: '#1890ff', marginLeft: 8 }}
                  />
                )}
              </div>
            </div>
          ))}
          
          {loading && (
            <div style={{ textAlign: 'center', padding: 16 }}>
              <Avatar 
                icon={<RobotOutlined />} 
                style={{ backgroundColor: '#722ed1' }}
              />
              <Text style={{ marginLeft: 8 }}>AI is thinking...</Text>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Questions */}
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #f0f0f0' }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Quick questions:</Text>
          <div style={{ marginTop: 8 }}>
            {quickQuestions.map((question, index) => (
              <Tag
                key={index}
                style={{ 
                  cursor: 'pointer', 
                  margin: '2px 4px 2px 0',
                  border: '1px solid #d9d9d9'
                }}
                onClick={() => setCurrentMessage(question)}
              >
                {question}
              </Tag>
            ))}
          </div>
        </div>

        {/* Input Area */}
        <div style={{ 
          padding: 16, 
          borderTop: '1px solid #f0f0f0',
          background: '#fafafa'
        }}>
          <Space.Compact style={{ width: '100%' }}>
            <TextArea
              value={currentMessage}
              onChange={(e) => setCurrentMessage(e.target.value)}
              placeholder="Ask about your study data..."
              autoSize={{ minRows: 1, maxRows: 4 }}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              style={{ flex: 1 }}
            />
            <Button 
              type="primary" 
              icon={<SendOutlined />}
              onClick={sendMessage}
              loading={loading}
              disabled={!currentMessage.trim()}
            />
          </Space.Compact>
        </div>
      </Card>

      {/* Functions Modal */}
      <Modal
        title="Analysis Functions"
        open={functionModalVisible}
        onCancel={() => setFunctionModalVisible(false)}
        footer={null}
        width={600}
      >
        <List
          dataSource={availableFunctions}
          renderItem={(func) => (
            <List.Item
              actions={[
                <Button 
                  type="primary"
                  onClick={() => callAnalysisFunction(func.name)}
                >
                  Run
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={<FunctionOutlined />}
                title={func.name.replace(/_/g, ' ').toUpperCase()}
                description={func.description}
              />
            </List.Item>
          )}
        />
      </Modal>
    </div>
  );
};

export default InsightConsole; 